import React, { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  DocumentPlusIcon,
  EyeIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { useAuth } from '@/hooks/auth';
import {
  useCounterpartySearch,
  useCounterpartyDetail,
  counterpartyOptionLabel,
} from '@/hooks/counterparty';
import { useBillingStatements } from '../hooks/use-billing-statements';
import { BillingFilters } from '../components/BillingFilters';
import {
  billingSearchToParams,
  billingStatementTypeOf,
  type BillingSearch,
} from '../billing-search';
import { BillingPreviewModal } from '../components/BillingPreviewModal';
import { GenerateStatementModal } from '../components/GenerateStatementModal';
import { MarkPaidModal } from '../components/MarkPaidModal';
import { ManualAdjustmentsModal } from '../components/ManualAdjustmentsModal';
import { BillingTable } from '../components/BillingTable';
import { BillingStatementExpandedRow } from '../components/BillingStatementExpandedRow';
import { createBillingColumns } from '../components/billing-columns';
import type { BillingStatementListItem } from '@/types';

// Toolbar buttons match the single-trade toolbar (TradeActionButtons): one filled
// primary verb, outline/blue for the "look elsewhere" action. Copied classnames,
// not a shared constant — two sites is below the abstraction threshold.
const PRIMARY_BTN =
  'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors';
const NAV_BTN =
  'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors';

export const BillingPage: React.FC = () => {
  // LR-557 polish: the modal owns the counterparty choice (mirrors
  // GenerateStatementModal), so the header carries no orphan select and the
  // page keeps no preview state beyond open/closed.
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  // Snapshot the row when Mark paid opens (LR-556): a list refetch while the
  // modal is on screen must not swap or clear its statement.
  const [markPaidFor, setMarkPaidFor] =
    useState<BillingStatementListItem | null>(null);
  // LR-557: expanded per-security rows. A Set, not single-open — comparing two
  // counterparties' slices side by side is the point of the overview.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Manual adjustments (LR-563). Open/closed only: like the MTD preview, the
  // modal owns the counterparty choice, so the header keeps no orphan select.
  const [isAdjustmentsOpen, setIsAdjustmentsOpen] = useState(false);

  // LR-551: the URL owns the filter state — no local mirror to drift out of sync,
  // and a filtered list is shareable / survives a reload. `replace` is deliberate:
  // tweaking the filter row should not push five history entries between the page
  // the user arrived from and Back.
  const filters: BillingSearch = useSearch({ from: '/billing' });
  const navigate = useNavigate();
  const setFilters = (next: BillingSearch) =>
    navigate({ to: '/billing', search: next, replace: true });

  const listParams = useMemo(() => billingSearchToParams(filters), [filters]);
  const { data, isLoading, error } = useBillingStatements(listParams);

  // LR-572: the filter's options are searched and paged server-side. The previous
  // `?limit=10000` pulled the whole registry (~16k rows, ~4.5 MB on the stands) to
  // filter it in the browser, and left this select reading "No results found" for
  // the entire round-trip.
  const [cpQuery, setCpQuery] = useState('');
  const {
    options: counterpartyOptions,
    isLoading: cpLoading,
    isError: cpError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCounterpartySearch(cpQuery);

  // A filtered counterparty is generally NOT in the loaded window (it may be
  // thousands of rows down, or not match the current query at all), so its name is
  // resolved on its own rather than looked up in the list. Labelled the same way
  // the list labels it: dropping the "(inactive)" marker here would hide the
  // distinction exactly when the filter is applied to a deactivated one.
  const { data: selectedCounterparty } = useCounterpartyDetail(filters.cp ?? 0);
  const selectedCounterpartyLabel = selectedCounterparty
    ? counterpartyOptionLabel(selectedCounterparty.name, selectedCounterparty.isActive)
    : undefined;

  // Carried into the MTD preview so it opens on what the page is already showing
  // (LR-624 follow-up): the customer's report came from `/billing?type=cash_pool`,
  // and a modal that ignores that filter opens onto the very tab they said was
  // empty. ACTIVE only, and the raw name rather than the option label: the
  // modal's own picker is active-only (a month-to-date read of a deactivated
  // counterparty has no product meaning), and its title is prose, not a list row,
  // so the "(inactive)" marker has no place in it.
  const previewCounterparty =
    filters.cp !== undefined &&
    selectedCounterparty !== undefined &&
    selectedCounterparty.isActive
      ? { id: filters.cp, name: selectedCounterparty.name }
      : undefined;

  // LR-680: billing writes are capability-gated, like the trade book
  // (TradesPage) and the registries (CounterpartiesPage). The backend enforces
  // it too (403 from `CanCreateTrades`); this hides the controls a view-only
  // user cannot use. Reads — MTD Preview, the adjustments list, statement
  // downloads — stay open, as they are on the API.
  const canTrade = useAuth().hasPermission('create_trades');

  const columns = useMemo(
    () =>
      createBillingColumns({
        // No handler, no Mark paid column at all (billing-columns.tsx).
        onMarkPaid: canTrade ? setMarkPaidFor : undefined,
        expansion: {
          isExpanded: (id) => expanded.has(id),
          onToggle: (id) =>
            setExpanded((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            }),
        },
      }),
    [expanded, canTrade]
  );
  const rows: BillingStatementListItem[] = data?.results ?? [];

  return (
    <div className="flex h-[calc(100vh-0px)] flex-col">
      <Card className="flex flex-1 flex-col overflow-hidden rounded-none border-none">
        <CardHeader className="flex flex-col gap-2 py-2">
          <div className="flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <span className="px-3 py-1.5 text-lg font-semibold text-gray-900">
                Billing Statements
              </span>
            </div>
            {/* Plain group, not role="toolbar": that role implies roving
                tabindex + arrow-key navigation (ARIA APG), which this handful
                of buttons doesn't warrant. */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsPreviewOpen(true)}
                className={NAV_BTN}
              >
                <EyeIcon className="h-4 w-4" />
                MTD Preview
              </button>
              <button
                onClick={() => setIsAdjustmentsOpen(true)}
                className={NAV_BTN}
              >
                <PencilSquareIcon className="h-4 w-4" />
                Manual Adjustments
              </button>
              {canTrade && (
                <button
                  onClick={() => setIsGenerateOpen(true)}
                  className={PRIMARY_BTN}
                >
                  <DocumentPlusIcon className="h-4 w-4" />
                  Generate Statement
                </button>
              )}
            </div>
          </div>

          <BillingFilters
            value={filters}
            onChange={setFilters}
            counterpartyOptions={counterpartyOptions}
            counterpartyName={selectedCounterpartyLabel}
            onCounterpartySearch={setCpQuery}
            isCounterpartyLoading={cpLoading}
            isCounterpartyError={cpError}
            hasMoreCounterparties={hasNextPage}
            // `cancelRefetch: false` so a burst of scroll events joins the page
            // already in flight instead of cancelling and re-issuing it.
            onLoadMoreCounterparties={() => fetchNextPage({ cancelRefetch: false })}
            isLoadingMoreCounterparties={isFetchingNextPage}
            resultCount={data?.count}
            shownCount={rows.length}
          />
        </CardHeader>

        <CardContent className="relative flex min-h-0 flex-1 flex-col overflow-hidden border-t border-gray-200 p-0">
          {error ? (
            <div className="flex items-center justify-center py-12 text-sm text-red-600">
              Failed to load billing statements.
            </div>
          ) : (
            <BillingTable
              data={rows}
              columns={columns}
              isLoading={isLoading}
              // Driven by the params actually sent, not by the filter state, so
              // it stays true as options are added: since LR-558 a selected
              // `type` really does narrow the request (it maps to
              // `statement_type`), and "your filters excluded rows" must match
              // what was actually filtered.
              isFiltered={Object.keys(listParams).length > 0}
              // LR-557: a row opens the counterparty's document page (the
              // detail outgrew the modal: currency blocks + four sections).
              onRowClick={(row) =>
                navigate({
                  to: '/billing/$statementId',
                  params: { statementId: String(row.id) },
                })
              }
              isExpanded={(row) => expanded.has(row.id)}
              renderExpanded={(row) => (
                <BillingStatementExpandedRow statement={row} />
              )}
            />
          )}
        </CardContent>
      </Card>

      {isPreviewOpen && (
        <BillingPreviewModal
          onClose={() => setIsPreviewOpen(false)}
          initialDocType={billingStatementTypeOf(filters.type)}
          initialCounterparty={previewCounterparty}
        />
      )}

      {isGenerateOpen && (
        <GenerateStatementModal onClose={() => setIsGenerateOpen(false)} />
      )}

      {markPaidFor && (
        <MarkPaidModal
          statement={markPaidFor}
          onClose={() => setMarkPaidFor(null)}
        />
      )}

      {isAdjustmentsOpen && (
        <ManualAdjustmentsModal onClose={() => setIsAdjustmentsOpen(false)} />
      )}
    </div>
  );
};
